import type {
  BillingTransactionStatsFilters,
  BillingTransactionsFilters,
  BillingTransactionsListResponse,
} from "@/types/billing-transactions";
import { useBillingTransactionContext } from "@/contexts/BillingTransactionsContext";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isSuperAdminRole, isTenantAdminRole } from "@/utils/accessControl";

const buildBillingTransactionStatsFilters = (
  apiFilters: BillingTransactionsFilters,
  effectiveHotelId?: string,
): BillingTransactionStatsFilters => ({
  ...(effectiveHotelId && { hotelId: effectiveHotelId }),
  ...(apiFilters.bookingId && {
    bookingId: apiFilters.bookingId,
  }),
  ...(apiFilters.fromDate && {
    fromDate: apiFilters.fromDate,
  }),
  ...(apiFilters.guestId && {
    guestId: apiFilters.guestId,
  }),
  ...(apiFilters.maxAmount !== undefined && {
    maxAmount: apiFilters.maxAmount,
  }),
  ...(apiFilters.minAmount !== undefined && {
    minAmount: apiFilters.minAmount,
  }),
  ...(apiFilters.paymentMethod && {
    paymentMethod: apiFilters.paymentMethod,
  }),
  ...(apiFilters.serviceId && {
    serviceId: apiFilters.serviceId,
  }),
  ...(apiFilters.serviceRequestId && {
    serviceRequestId: apiFilters.serviceRequestId,
  }),
  ...(apiFilters.status && {
    status: apiFilters.status,
  }),
  ...(apiFilters.toDate && {
    toDate: apiFilters.toDate,
  }),
  ...(apiFilters.type && {
    type: apiFilters.type,
  }),
});

export const useBillingTransactionsFiltering = (hotelId?: string) => {
  const { filters, actions, computed, useBillingTransactions } =
    useBillingTransactionContext();
  const { userProfile, isLoading: isUserLoading } = useAuthenticatedUser();
  const hasCrossHotelAccess =
    isTenantAdminRole(userProfile) || isSuperAdminRole(userProfile);
  const isAllHotelsSelected = filters.hotelId === "all hotels";
  const selectedHotelId =
    filters.hotelId && !isAllHotelsSelected ? filters.hotelId : undefined;
  let effectiveHotelId = selectedHotelId;

  if (!isUserLoading && !hasCrossHotelAccess) {
    effectiveHotelId = hotelId;
  }

  const appliedFilters: BillingTransactionsFilters = {
    ...computed.apiFilters,
    ...(effectiveHotelId && { hotelId: effectiveHotelId }),
  };

  const statsFilters = buildBillingTransactionStatsFilters(
    computed.apiFilters,
    effectiveHotelId,
  );

  const billingTransactionsQuery = useBillingTransactions(
    filters.currentPage,
    computed.limit,
    appliedFilters,
  );

  const billingTransactionsData =
    (billingTransactionsQuery.data as
      | BillingTransactionsListResponse
      | undefined) ??
    ({
      transactions: [],
      summary: {
        totalCount: 0,
        totalAmount: 0,
        succeededCount: 0,
        failedCount: 0,
        pendingCount: 0,
      },
      total: 0,
      page: filters.currentPage,
      limit: computed.limit,
      totalPages: 1,
    } satisfies BillingTransactionsListResponse);

  const billingTransactions = billingTransactionsData.transactions;

  const totalPages =
    billingTransactionsData.totalPages ??
    Math.ceil((billingTransactionsData.total || 0) / computed.limit);

  const allIds = billingTransactions.map((t) => t.id);

  return {
    billingTransactionsData, // якщо потрібно summary/total/...
    billingTransactions,
    totalPages,
    isLoading: billingTransactionsQuery.isLoading,
    error: billingTransactionsQuery.error,

    filters,
    actions,
    computed,
    appliedFilters,
    statsFilters,

    pagination: {
      currentPage: filters.currentPage,
      totalPages,
      hasNextPage: filters.currentPage < totalPages,
      hasPrevPage: filters.currentPage > 1,
      goToNextPage: () => {
        if (filters.currentPage < totalPages) {
          actions.setCurrentPage(filters.currentPage + 1);
        }
      },
      goToPrevPage: () => {
        if (filters.currentPage > 1) {
          actions.setCurrentPage(filters.currentPage - 1);
        }
      },
      goToPage: (page: number) => {
        if (page >= 1 && page <= totalPages) {
          actions.setCurrentPage(page);
        }
      },
    },

    selection: {
      selectedBillingTransactions: filters.selectedTransactions,
      isAllSelected:
        billingTransactions.length > 0 &&
        filters.selectedTransactions.length === billingTransactions.length,
      selectedCount: filters.selectedTransactions.length,

      selectTransaction: actions.handleSelectTransaction,

      selectAll: (checked: boolean) => actions.handleSelectAll(checked, allIds),

      clearSelection: () => actions.setSelectedTransactions([]),
    },
  };
};
